import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, AlertCircle, TrendingDown, Clock, AlertTriangle, Download, Printer, FileText } from 'lucide-react';
import { DataStore } from '@/lib/data-store';
import { calculateHutang, calculateDebtSummary, getStatusColor } from '@/lib/debt-utils';
import { exportToExcel, printTable } from '@/lib/export-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';

const HutangPelanggan = () => {
  const data = DataStore.getData();
  const hutangs = calculateHutang(data.invoices);
  const summary = calculateDebtSummary(hutangs);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const filteredHutangs = hutangs.filter(h => {
    const matchSearch = h.pelanggan.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       h.noInvoice.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = !filterStatus || h.status === filterStatus;
    return matchSearch && matchFilter;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">7. Hutang Pelanggan</h1>
          <p className="text-slate-600 mt-1">Pantau piutang dan pembayaran pelanggan secara real-time</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {hutangs.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(filteredHutangs, [
                    { label: 'Pelanggan', key: 'pelanggan' },
                    { label: 'No. Invois', key: 'noInvoice' },
                    { label: 'Tarikh Invois', key: 'tarikhInvoice' },
                    { label: 'Total Hutang (RM)', key: 'totalHutang', format: (v) => `RM ${v.toFixed(2)}` },
                    { label: 'Terbayar (RM)', key: 'terbayar', format: (v) => `RM ${v.toFixed(2)}` },
                    { label: 'Sisa (RM)', key: 'sisa', format: (v) => `RM ${v.toFixed(2)}` },
                    { label: 'Status', key: 'status' }
                  ]);
                  printWithLetterhead('DAFTAR HUTANG PELANGGAN', tableHTML, 'PIUTANG / HUTANG PELANGGAN');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(filteredHutangs, 'Hutang Pelanggan')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <p className="text-slate-600 text-sm font-medium">Jumlah Hutang</p>
                <AlertCircle className="text-red-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-slate-900">RM {summary.totalSisa.toFixed(2)}</p>
              <p className="text-xs text-slate-500">dari RM {summary.totalHutang.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <p className="text-slate-600 text-sm font-medium">Sudah Terbayar</p>
                <TrendingDown className="text-green-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-green-600">RM {summary.totalTerbayar.toFixed(2)}</p>
              <p className="text-xs text-slate-500">{((summary.totalTerbayar / summary.totalHutang) * 100).toFixed(1)}% pembayaran</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <p className="text-slate-600 text-sm font-medium">Tertunggak</p>
                <Clock className="text-yellow-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-yellow-600">{summary.jumlahTertunggak}</p>
              <p className="text-xs text-slate-500">pelanggan dengan tunggakan</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <p className="text-slate-600 text-sm font-medium">Urgent</p>
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <p className="text-2xl font-bold text-red-600">{summary.urgent}</p>
              <p className="text-xs text-slate-500">perlu tindakan segera</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <Input 
                placeholder="Cari pelanggan atau no. invois..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-300"
              />
            </div>
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Semua Status</option>
              <option value="Urgent">Urgent</option>
              <option value="Tertunggak">Tertunggak</option>
              <option value="Sebagian">Sebagian Terbayar</option>
              <option value="Lunas">Lunas</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Hutang Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daftar Hutang Pelanggan</CardTitle>
          <CardDescription>Total {filteredHutangs.length} catatan hutang (Auto-generate dari Invoice)</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredHutangs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Tiada data hutang. Semua invois sudah lunas.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table id="hutang-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Pelanggan</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">No. Invois</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Tarikh Invois</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Total Hutang (RM)</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Terbayar (RM)</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Sisa Hutang (RM)</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Jatuh Tempo</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-700">Hari Terlambat</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHutangs.map((hutang) => (
                    <tr key={hutang.id} className={`border-b border-slate-100 hover:bg-slate-50 ${hutang.status === 'Urgent' ? 'bg-red-50' : ''}`}>
                      <td className="py-3 px-4 font-medium text-slate-900">{hutang.pelanggan}</td>
                      <td className="py-3 px-4 text-slate-600">{hutang.noInvoice}</td>
                      <td className="py-3 px-4 text-slate-600">{hutang.tarikhInvoice}</td>
                      <td className="py-3 px-4 text-right font-medium text-slate-900">RM {hutang.totalHutang.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right text-green-600 font-medium">RM {hutang.terbayar.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-bold text-red-600">RM {hutang.sisa.toFixed(2)}</td>
                      <td className="py-3 px-4 text-slate-600">{hutang.jatuhTempo}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-medium ${
                          hutang.hariTerlambat > 7 ? 'text-red-600' :
                          hutang.hariTerlambat > 0 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {hutang.hariTerlambat > 0 ? `+${hutang.hariTerlambat}` : 'Tepat'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(hutang.status)}`}>
                          {hutang.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HutangPelanggan;